import { Box, HStack, Text, useColorModeValue } from '@chakra-ui/react';
import { Avatar } from '../Avatar';
import { useT } from '../../i18n';

export interface MentionCandidate {
  id: string;
  handle: string;
  displayName: string;
  avatarUrl: string | null;
}

// The `@query` being typed right before the caret, or null (M22, D27).
export function mentionQueryAt(text: string, caret: number): string | null {
  const match = /(?:^|[^A-Za-z0-9_@])@([A-Za-z0-9_]{0,30})$/.exec(
    text.slice(0, caret)
  );
  return match ? match[1]!.toLowerCase() : null;
}

/** People matching `query` by handle prefix or name, best matches first. */
export function filterMentionCandidates(
  candidates: MentionCandidate[],
  query: string,
  limit = 6
): MentionCandidate[] {
  const q = query.toLowerCase();
  const scored = candidates
    .map((candidate) => {
      const name = candidate.displayName.toLowerCase();
      const score = candidate.handle.startsWith(q)
        ? 0
        : name.split(/\s+/).some((word) => word.startsWith(q))
          ? 1
          : name.includes(q)
            ? 2
            : -1;
      return { candidate, score };
    })
    .filter(({ score }) => score >= 0)
    .sort(
      (a, b) =>
        a.score - b.score ||
        a.candidate.displayName.localeCompare(b.candidate.displayName)
    );
  return scored.slice(0, limit).map(({ candidate }) => candidate);
}

/** Replaces the `@query` before the caret with `@handle `. */
export function insertMention(
  text: string,
  caret: number,
  handle: string
): { text: string; caret: number } {
  const before = text.slice(0, caret);
  const at = before.lastIndexOf('@');
  const inserted = `@${handle} `;
  return {
    text: before.slice(0, at) + inserted + text.slice(caret),
    caret: at + inserted.length,
  };
}

interface MentionPickerProps {
  candidates: MentionCandidate[];
  /** The chat's family: faces open through it (D292). */
  familyId: string | undefined;
  activeIndex: number;
  onPick: (candidate: MentionCandidate) => void;
}

export function MentionPicker({
  candidates,
  familyId,
  activeIndex,
  onPick,
}: MentionPickerProps) {
  const t = useT();
  const bg = useColorModeValue('white', 'gray.700');
  const activeBg = useColorModeValue('brand.50', 'brand.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  if (candidates.length === 0) return null;

  return (
    <Box
      role="listbox"
      aria-label={t('chat.mention.pick')}
      position="absolute"
      bottom="100%"
      left={{ base: 2, md: 4 }}
      right={{ base: 2, md: 4 }}
      mb={1}
      maxW="360px"
      bg={bg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      boxShadow="lg"
      py={1}
      zIndex={3}
    >
      {candidates.map((candidate, index) => (
        <HStack
          key={candidate.id}
          role="option"
          aria-selected={index === activeIndex}
          spacing={3}
          px={3}
          py={2}
          cursor="pointer"
          bg={index === activeIndex ? activeBg : undefined}
          // Keep the textarea focused (and the phone keyboard up).
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => onPick(candidate)}
        >
          <Avatar
            size="xs"
            name={candidate.displayName}
            src={candidate.avatarUrl ?? undefined}
            ownerId={candidate.id}
            familyId={familyId}
          />
          <Text fontSize="sm" fontWeight="medium" noOfLines={1}>
            {candidate.displayName}
          </Text>
          <Text fontSize="sm" color="gray.500" noOfLines={1}>
            @{candidate.handle}
          </Text>
        </HStack>
      ))}
    </Box>
  );
}
